import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SectionHeader } from '@/components/home/CoreSections';

export { SectionHeader };

export const INPUT_CLS = "bg-white border-black/15 text-[#121212] placeholder:text-[#999] focus:border-[#EE5A01] focus-visible:ring-[#EE5A01]/30 rounded-lg h-12";
export const SELECT_CONTENT_CLS = "bg-white border-black/10 rounded-lg shadow-xl";
export const SELECT_ITEM_CLS = "text-[#121212] focus:bg-[#FFF4ED] focus:text-[#EE5A01] rounded-md";
export const LABEL_CLS = "font-heading font-bold text-[11px] tracking-[0.12em] text-[#121212] uppercase mb-2 block";
export const ERROR_CLS = "font-body text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3";

const ctaClass = (variant) => ({ primary: 'btn-primary', secondary: 'btn-secondary', outline: 'btn-outline !border-white !text-white hover:!bg-white hover:!text-[#121212]' }[variant || 'primary']);

export function Cta({ cta, className = '' }) {
  const cls = `${cta.className || ctaClass(cta.variant)} ${className}`;
  if (cta.onClick) return <button type="button" onClick={cta.onClick} data-testid={cta.testid} className={cls}>{cta.label}</button>;
  if (cta.mailto) return <a href={cta.href} data-testid={cta.testid} className={cls}>{cta.label}</a>;
  if (cta.external) return <a href={cta.href} target="_blank" rel="noopener noreferrer" data-testid={cta.testid} className={cls}>{cta.label}</a>;
  return <Link to={cta.href} data-testid={cta.testid} className={cls}>{cta.label}</Link>;
}

export function BrandHero({ chip, title, lead, image, imageAlt, logo, logoWhite = false, logoAlt = '', ctas = [], testid, position = 'center', children }) {
  return (
    <section data-testid={testid} className="relative overflow-hidden bg-[#121212] text-white">
      <img src={image} alt={imageAlt || title} className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: position }} />
      <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-black/80 via-black/50 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
        {logo && <img src={logo} alt={logoAlt} className={logoWhite ? "h-10 sm:h-11 w-auto mb-6" : "h-9 w-auto mb-6 rounded-md bg-white/95 px-3 py-1.5"} />}
        {chip && <span className="chip-orange">{chip}</span>}
        <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-white max-w-3xl">{title}</h1>
        {lead && <p className="mt-5 font-body text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">{lead}</p>}
        {ctas.length > 0 && <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">{ctas.map((c) => <Cta key={c.label} cta={c} />)}</div>}
        {children}
      </div>
    </section>
  );
}

export function StatStrip({ stats, testid }) {
  const cols = stats.length > 4 ? 'sm:grid-cols-3 lg:grid-cols-6' : 'lg:grid-cols-4';
  return (
    <section data-testid={testid} className="bg-white border-b border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-2 ${cols} gap-px bg-black/5`}>
          {stats.map((s) => (
            <div key={s.label} className="bg-white py-7 lg:py-8 px-5 lg:px-7">
              <p className="font-heading font-black text-2xl lg:text-3xl text-[#121212] leading-none tracking-tight">{s.value}</p>
              <p className="mt-2 font-body text-xs lg:text-sm text-[#666666]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Prose({ chip, title, paragraphs, bg = 'bg-[#FAFAFA]', children }) {
  return (
    <section className={`${bg} py-14 sm:py-20`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader compact chip={chip} title={title} />
        <div className="mt-6 space-y-4 font-body text-base text-[#555555] leading-relaxed">{paragraphs.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}</div>
        {children}
      </div>
    </section>
  );
}

export function FeatureGrid({ chip, title, desc, items, cols = 3, bg = 'bg-white', testid, align = 'center', itemTestId }) {
  const colCls = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-2 lg:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[cols];
  return (
    <section data-testid={testid} className={`${bg} py-14 sm:py-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && <SectionHeader compact chip={chip} title={title} desc={desc} align={align} />}
        <div className={`${title ? 'mt-10' : ''} grid grid-cols-1 ${colCls} gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]`}>
          {items.map((f, i) => (
            <div key={f.title} data-testid={itemTestId ? `${itemTestId}-${i}` : undefined} className="bg-white p-6 lg:p-7 transition-colors hover:bg-[#FFF4ED]/70">
              {f.icon && <f.icon className="w-5 h-5 text-[#EE5A01]" />}
              <h3 className={`${f.icon ? 'mt-5' : ''} font-heading font-bold text-base text-[#121212]`}>{f.title}</h3>
              <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SplitSection({ chip, title, desc, image, imageAlt, reverse = false, bg = 'bg-white', testid, children, imageClass = 'h-72 sm:h-[420px]' }) {
  return (
    <section data-testid={testid} className={`${bg} py-14 sm:py-20 overflow-x-hidden`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <div className="overflow-hidden rounded-xl border border-black/[0.06]">
          <img src={image} alt={imageAlt || title} loading="lazy" className={`${imageClass} w-full object-cover`} />
        </div>
        <div>
          <SectionHeader compact chip={chip} title={title} desc={desc} />
          {children}
        </div>
      </div>
    </section>
  );
}

export function CheckList({ items, className = '' }) {
  return (
    <ul className={`space-y-3 ${className}`}>
      {items.map((t) => (
        <li key={t} className="flex items-start gap-3 font-body text-sm text-[#121212]">
          <span className="mt-0.5 grid h-5 w-5 place-items-center rounded-md bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><Check className="w-3 h-3" /></span>{t}
        </li>
      ))}
    </ul>
  );
}

export function CompareTable({ columns, rows, highlight, accent = '#EE5A01', testid }) {
  return (
    <div data-testid={testid} className="overflow-hidden rounded-xl border border-black/10 bg-white text-xs sm:text-sm">
      <div className="grid bg-[#F5F5F3]" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
        {columns.map((c, i) => (
          <div key={c || 'feature'} className={`p-3 font-heading font-bold uppercase tracking-wider text-[10px] sm:text-[11px] ${i === 0 ? 'text-[#666666]' : 'text-center'}`} style={i !== 0 && i === highlight ? { color: accent } : undefined}>{c}</div>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row[0]} className="grid border-t border-black/5" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
          {row.map((cell, i) => (
            <div key={`${row[0]}-${i}`} className={`p-3 font-body ${i === 0 ? 'font-semibold text-[#121212]' : 'text-center text-[#555555]'} ${i === highlight ? 'bg-[#FFF4ED]/60 text-[#121212]' : ''}`}>{cell}</div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function LogoStrip({ title, logos, className = '' }) {
  return (
    <section className={`bg-white py-12 border-y border-black/5 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center font-heading font-bold text-[11px] tracking-[0.2em] uppercase text-[#666666]">{title}</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {logos.map((l, i) => typeof l === 'string' && l.startsWith('http') || (typeof l === 'string' && l.startsWith('/'))
            ? <img key={l} src={l} alt={`Client ${i + 1}`} loading="lazy" className="h-9 sm:h-11 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all" />
            : <span key={l} className="font-heading font-bold text-sm text-[#999] hover:text-[#121212] uppercase tracking-wider transition-colors">{l}</span>)}
        </div>
      </div>
    </section>
  );
}

export function CtaBand({ eyebrow = 'We Move You!', title, desc, ctas = [], testid }) {
  return (
    <section data-testid={testid} className="relative overflow-hidden bg-[#EE5A01] py-14 sm:py-16">
      <div className="absolute inset-y-0 end-0 w-1/2 bg-[#F17B34] [clip-path:polygon(30%_0,100%_0,100%_100%,0_100%)] rtl:[clip-path:polygon(0_0,70%_0,100%_100%,0_100%)]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <div className="max-w-2xl">
          <p className="font-heading font-black text-sm tracking-[0.2em] uppercase text-white/80">{eyebrow}</p>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-white leading-[1.1] tracking-tight">{title}</h2>
          {desc && <p className="mt-4 font-body text-base text-white/85 max-w-lg">{desc}</p>}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {ctas.map((c) => <Cta key={c.label} cta={{ ...c, variant: c.variant || 'secondary' }} />)}
        </div>
      </div>
    </section>
  );
}

export function InlineLink({ to, children }) {
  return <Link to={to} className="inline-flex items-center gap-1 font-heading font-bold text-sm text-[#EE5A01] hover:gap-2 transition-all">{children} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></Link>;
}
